/**
 * The console's data view of a managed Postgres (infrastructure/db-helper/DESIGN_V1.md D13): when the Actions menu
 * offers it, how an answer or a refusal is read, and how the rows grid pages.
 *
 * ⚠️ ITS OWN MODULE, WITH NO IMPORTS, ON PURPOSE (haUpgradeOption.ts tells the story): a module that imports
 *    axiosInstance drags in keycloak-js, which CRA's jest does not transform, so the rules below could not be tested.
 *
 * ⛔ CUSTOMER DATA IS TEXT. Every cell, table name, column name and Postgres error comes from the customer's own
 *    database, so the components render it as React text only, never as markup (ResultGrid.test.tsx pins this).
 *    Nothing here or in the components stores it: no Redux, no browser storage, component state only.
 */

// ── The API's answers (backend services/cloud/dataView.ts, dataViewAnswer) ──────────────────────────────────────

export interface DataColumn { name: string; type: string }

/** Postgres relkind: table, view, materialized view, partitioned table, foreign table. */
export type DataTableKind = 'r' | 'v' | 'm' | 'p' | 'f';

export interface DataTable {
    schema: string;
    name: string;
    kind: DataTableKind;
    /** Postgres's estimate, GREATEST(reltuples, 0): 0 also means "never analyzed", so it never means "empty". */
    estimated_rows: number;
    columns: DataColumn[];
}

/** One grid of cells: a page of a table's rows, or a query's result. Every cell is text or null. */
export interface DataResult {
    columns: string[];
    rows: (string | null)[][];
    /** The helper stopped early: at 500 rows, or once the answer reached about 1 MB. */
    truncated: boolean;
}

/** The rows grid pages 50 at a time (DESIGN_V1.md D13); the API allows 1 to 500. */
export const PAGE_SIZE = 50;
/** The furthest offset the API accepts (backend dataViewRowsQuerySchema). */
export const MAX_OFFSET = 10000000;
/** Every data call waits this long (D13). The backend's own worst case is a 5 s lookup plus a 25 s SSH budget. */
export const REQUEST_TIMEOUT_MS = 30000;
/** The SQL box's cap, the same as the backend's (dataViewQueryBodySchema). */
export const SQL_MAX_CHARS = 50000;

// ── The Actions entry ────────────────────────────────────────────────────────────────────────────────────────────

/** What this module reads from a database row. */
export interface DataViewRow {
    status: string;
    /** D6: absent when the data view is off for the account; false when this database has no working helper. */
    data_view?: boolean;
    ha?: boolean;
}

/**
 * The "View data" entry for a row's Actions menu, or `null` when the data view is off for this account (the API then
 * leaves `data_view` out, D6, and the console offers nothing it cannot do).
 *
 * ⛔ LOCKED, NOT HIDDEN, with the reason IN THE LABEL (the haUpgradeOption rule: a disabled react-select option has
 *    nowhere else to carry one). `data_view: false` covers three states the console cannot tell apart: a database made
 *    before the data view, one whose setup is still running, and one whose setup failed. "Not set up" is true of all
 *    three; "new databases only" would be false for the two that ARE new.
 */
export const dataViewOption = (
    row: DataViewRow,
): { value: 'data'; label: string; isDisabled?: boolean } | null => {
    if (row.data_view === undefined) return null;
    if (row.status !== 'ready') return { value: 'data', label: 'View data (not ready)', isDisabled: true };
    if (row.data_view !== true) return { value: 'data', label: 'View data (not set up)', isDisabled: true };
    return { value: 'data', label: 'View data' };
};

// ── The unavailable state ────────────────────────────────────────────────────────────────────────────────────────

// The console's words are one short line per case, with no explanation under them (Milos, 2026-10-24: "too much
// text", "non premium"). The API's own messages and hints are written for API callers and stay in the API.

export const UNAVAILABLE_TITLE = 'Data unavailable';

/** On an HA database every read goes through one member, which may be a replica (helper README, measured on DB 348). */
export const HA_READ_NOTE = 'Reads may lag slightly behind writes.';

export const haReadNote = (row: { ha?: boolean }): string | null => (row.ha ? HA_READ_NOTE : null);

/**
 * Why the view cannot read this database, known from the row alone, so no request is sent; null when it may try.
 * The Actions entry is locked in these cases, so this answers for a row that changed while the view was open.
 */
export const rowUnavailable = (row: DataViewRow): { message: string } | null => {
    if (row.data_view === undefined) return { message: 'Not available for this account yet.' };
    if (row.status !== 'ready') return { message: 'Available once the database is ready.' };
    if (row.data_view !== true) return { message: 'Not set up on this database yet.' };
    return null;
};

// ── Refusals and failures ────────────────────────────────────────────────────────────────────────────────────────

/**
 * - `unavailable`: no call can work right now (the machine did not answer, the helper is missing, the database is
 *   gone or not ready). One helper serves the whole database, so the WHOLE view switches to the unavailable state.
 * - `query`: Postgres refused the statement. Its own text goes under the SQL box, as text.
 * - `other`: a refusal of this one request (a table dropped meanwhile, the rate limit, a held account): shown in its
 *   own pane, with the rest of the view still working.
 */
export type DataFailureKind = 'unavailable' | 'query' | 'other';

export interface DataFailure {
    kind: DataFailureKind;
    /** The API's error code, or the console's own for a call that got no answer. */
    code: string | null;
    /** One short line in the console's words. */
    message: string;
    /** query_failed only: Postgres's own error text, which expert mode's SQL box shows. */
    detail?: string;
    /** The API's own sentence, kept for a tooltip; never shown as a paragraph. */
    apiMessage?: string;
}

/** Codes that mean no call to this database can work right now (backend D9, and errorEnvelope's defaults). */
const WHOLE_VIEW_CODES: ReadonlySet<string> = new Set([
    'data_view_unavailable', 'database_unavailable', 'data_view_disabled', 'data_view_not_ready',
    'data_view_not_installed', 'not_found', 'service_unavailable', 'bad_gateway',
]);

const SHORT: Readonly<Record<string, string>> = {
    data_view_disabled: 'Not available for this account yet.',
    data_view_not_ready: 'Available once the database is ready.',
    data_view_not_installed: 'Not set up on this database yet.',
    not_found: 'Database not found.',
    table_not_found: 'This table no longer exists.',
    answer_too_large: 'Too large to show.',
    rate_limited: 'Too many requests. Try again in a moment.',
};
export const UNREACHABLE = "Can't reach this database.";

/** What Postgres's refusal of a table read means to someone not reading SQL (expert mode shows Postgres's own text). */
const queryMessage = (postgresError: string | undefined): string => {
    if (postgresError && /permission denied/i.test(postgresError)) return 'No access to this table.';
    if (postgresError && /statement timeout/i.test(postgresError)) return 'Took too long.';
    return 'Could not read this.';
};

const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

/**
 * Read a rejected data call (an axios error) into what the view shows. Duck-typed, so this module needs no import.
 * Postgres's text for query_failed is `detail` (the API keeps it under details.postgres_error, out of the message,
 * which the platform rewrites on every error).
 */
export const classifyDataError = (err: any): DataFailure => {
    const response = err?.response;
    if (!response) {
        if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') {
            return { kind: 'unavailable', code: 'timeout', message: 'No answer from the database.' };
        }
        return { kind: 'unavailable', code: 'no_response', message: 'No connection.' };
    }
    const status = Number(response.status) || 0;
    const envelope = response.data?.error;
    const code = text(envelope?.code) ?? null;
    const apiMessage = text(envelope?.message) ?? text(envelope);
    if (code === 'query_failed') {
        const detail = text(envelope?.details?.postgres_error);
        return { kind: 'query', code, message: queryMessage(detail), detail };
    }
    const short = code !== null ? SHORT[code] : undefined;
    if ((code !== null && WHOLE_VIEW_CODES.has(code)) || status >= 500) {
        return { kind: 'unavailable', code, message: short ?? UNREACHABLE, apiMessage };
    }
    if (status === 429) return { kind: 'other', code, message: SHORT.rate_limited, apiMessage };
    return { kind: 'other', code, message: short ?? 'Request refused.', apiMessage };
};

/** Whether Retry can help: not for a refusal that would only repeat (no access, a table that is gone, a bad name). */
export const retryable = (f: DataFailure): boolean =>
    f.kind !== 'query' && f.code !== 'table_not_found' && f.code !== 'unaddressable_name';

/** A 200 that is not the shape the API sends (a proxy page, an older backend): read as unavailable, never rendered. */
export const BAD_ANSWER: DataFailure = {
    kind: 'unavailable', code: 'bad_answer',
    message: 'Unexpected answer. Reload the page.',
};

// ── Reading answers (defensive: a cell that is not text must never reach React as an object) ────────────────────

const KINDS: ReadonlySet<string> = new Set(['r', 'v', 'm', 'p', 'f']);

/** GET .../data/tables, or null when the answer is not that shape. */
export const readTables = (data: any): DataTable[] | null => {
    if (!data || !Array.isArray(data.tables)) return null;
    const out: DataTable[] = [];
    for (const t of data.tables) {
        if (!t || typeof t.schema !== 'string' || typeof t.name !== 'string') return null;
        out.push({
            schema: t.schema,
            name: t.name,
            kind: KINDS.has(t.kind) ? t.kind : 'r',
            estimated_rows: Number.isFinite(t.estimated_rows) && t.estimated_rows > 0 ? Math.trunc(t.estimated_rows) : 0,
            columns: Array.isArray(t.columns)
                ? t.columns.filter((c: any) => c && typeof c.name === 'string').map((c: any) => ({ name: c.name, type: typeof c.type === 'string' ? c.type : '' }))
                : [],
        });
    }
    return out;
};

/** A rows page or a query result, or null when the answer is not that shape. Cells become text or null. */
export const readResult = (data: any): DataResult | null => {
    if (!data || !Array.isArray(data.columns) || !Array.isArray(data.rows)) return null;
    if (!data.rows.every((r: unknown) => Array.isArray(r))) return null;
    return {
        columns: data.columns.map((c: unknown) => String(c)),
        rows: data.rows.map((r: unknown[]) => r.map((cell) => (cell === null || cell === undefined ? null : String(cell)))),
        truncated: data.truncated === true,
    };
};

// ── The table list ───────────────────────────────────────────────────────────────────────────────────────────────

/** A table's name as the list shows it: the schema only when it is not `public`. */
export const tableLabel = (t: { schema: string; name: string }): string =>
    (t.schema === 'public' ? t.name : `${t.schema}.${t.name}`);

/** A stable key for a table (schema and name are both free text, so neither alone, nor a joined string, is safe). */
export const tableKey = (t: { schema: string; name: string }): string => JSON.stringify([t.schema, t.name]);

const KIND_LABELS: Readonly<Record<string, string>> = {
    v: 'view', m: 'materialized view', p: 'partitioned table', f: 'foreign table',
};

/** Shown next to anything that is not a plain table. */
export const kindLabel = (kind: string): string | null => KIND_LABELS[kind] ?? null;

/** The filter box: `schema.name`, in any letter case. */
export const filterTables = (tables: DataTable[], query: string): DataTable[] => {
    const q = query.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter((t) => `${t.schema}.${t.name}`.toLowerCase().includes(q));
};

export const groupThousands = (n: number): string => String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "~1,234 rows", only when Postgres's estimate is above 0 (0 can mean "never analyzed", so it is not shown as empty). */
export const approxRows = (estimated: number): string | null =>
    (estimated > 0 ? `~${groupThousands(estimated)} ${estimated === 1 ? 'row' : 'rows'}` : null);

/** A column's type for the grid header: from the tables answer, because the rows answer carries names only. */
export const columnTypes = (resultColumns: string[], tableColumns: DataColumn[]): (string | null)[] =>
    resultColumns.map((name, i) => {
        if (tableColumns[i]?.name === name) return tableColumns[i].type || null;
        return tableColumns.find((c) => c.name === name)?.type || null;
    });

// ── The rows grid ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The rows call for one table, or null when its name cannot travel in a URL path: a browser removes a `.` or `..`
 * path segment even when it is written as %2e, so such a request would reach another route. Every other name is
 * sent through encodeURIComponent.
 */
export const rowsUrl = (databaseId: number, schema: string, table: string): string | null => {
    if ([schema, table].some((s) => s === '.' || s === '..')) return null;
    return `/v1/databases/${databaseId}/data/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/rows`;
};

/**
 * DESIGN_EDITING.md E10: the page and search calls, which sort and find use, and info, cell and rows/delete, which
 * editing uses (same path rule as rowsUrl). Add row and Edit row POST and PATCH rowsUrl itself.
 */
const tableCall = (databaseId: number, schema: string, table: string, last: 'page' | 'search' | 'info' | 'cell' | 'rows/delete' | 'lookup'): string | null => {
    const rows = rowsUrl(databaseId, schema, table);
    return rows ? `${rows.slice(0, -'/rows'.length)}/${last}` : null;
};
export const pageUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'page');
export const searchUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'search');
export const infoUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'info');
export const cellUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'cell');
/** E27: the rows a foreign key points at. */
export const lookupUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'lookup');
export const rowsDeleteUrl = (databaseId: number, schema: string, table: string): string | null => tableCall(databaseId, schema, table, 'rows/delete');

/** The find box's limit (the backend's). */
export const FIND_MAX_CHARS = 200;

/** How long typing pauses before the find runs (Enter runs it at once). */
export const FIND_DELAY_MS = 400;

/** The column Find starts on: the first text column (where a typed word is usually looked for), else the first. */
export const defaultFindColumn = (columns: DataColumn[]): string | null =>
    (columns.find((c) => /^(text|character|citext)\b/.test(c.type)) ?? columns[0])?.name ?? null;

export interface GridSort { column: string; dir: 'asc' | 'desc' }

/** A header click (console flow 1): ascending, then descending, then not sorted; another column starts ascending. */
export const nextSort = (current: GridSort | null, column: string): GridSort | null => {
    if (!current || current.column !== column) return { column, dir: 'asc' };
    return current.dir === 'asc' ? { column, dir: 'desc' } : null;
};

/** A page or search answer: a result, plus its key and the cells the helper cut; null when it is not that shape. */
export const readPage = (data: any): (DataResult & { key: string[]; cut: [number, number][] }) | null => {
    const result = readResult(data);
    if (!result || !Array.isArray(data.key) || !Array.isArray(data.cut)) return null;
    if (!data.key.every((k: unknown) => typeof k === 'string')) return null;
    const cut = data.cut.filter((c: unknown) => Array.isArray(c) && c.length === 2 && c.every((n) => Number.isInteger(n) && n >= 0))
        .map((c: number[]) => [c[0], c[1]] as [number, number]);
    if (cut.length !== data.cut.length) return null;
    return { ...result, key: data.key, cut };
};

/** What the rows pane says, without sending anything, for a name rowsUrl cannot carry. */
export const UNADDRESSABLE_TABLE: DataFailure = {
    kind: 'other', code: 'unaddressable_name',
    message: "This table can't be opened here.",
};

/**
 * Whether there may be a next page, and where it starts. The rows answer has no total, so a full page means "maybe
 * more"; a page the helper cut short for size has fewer rows than asked for, so the next one starts after the rows
 * actually received, never at offset + limit (which would skip rows).
 */
export const nextPage = (offset: number, page: { rows: unknown[]; truncated: boolean }, limit = PAGE_SIZE):
    { hasNext: boolean; nextOffset: number } => {
    const nextOffset = offset + page.rows.length;
    const more = page.rows.length >= limit || (page.truncated && page.rows.length > 0);
    return { hasNext: more && nextOffset <= MAX_OFFSET, nextOffset };
};

/** "Rows 51-100": the page's own range, with no "of N" (the API has no total). */
export const rowsLabel = (offset: number, count: number): string => {
    if (count > 0) return `Rows ${groupThousands(offset + 1)}-${groupThousands(offset + count)}`;
    return offset > 0 ? 'No more rows' : 'No rows';
};

// ── The databases page's wait for a new machine's setup ──────────────────────────────────────────────────────────

/** How long, and how often, the databases page re-reads a new single machine whose data view setup has not reported. */
export const SETUP_WATCH_MS = 15 * 60 * 1000;
export const SETUP_WATCH_EVERY_MS = 15 * 1000;

/** A re-read ends the wait once data_view is no longer false: true (installed), or absent (the switch went off). */
export const setupSettled = (dataView: unknown): boolean => dataView !== false;

// ── Expert mode ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The Expert mode checkbox (Milos, 2026-10-24): checked, the SQL box opens. Unchecked by default. */
export const EXPERT_MODE_KEY = 'redu.dataView.expert';

/**
 * Whether this browser last checked Expert mode. It is a preference, not something read from the database, so it may
 * live in browser storage (rows never do). A store that is missing or throws (a private window, blocked site data)
 * means unchecked.
 */
export const readExpertMode = (storage: Pick<Storage, 'getItem'> | null | undefined): boolean => {
    try {
        return storage?.getItem(EXPERT_MODE_KEY) === 'on';
    } catch {
        return false;
    }
};

/** Remembers the checkbox; a store that refuses only means it is not remembered. */
export const writeExpertMode = (storage: Pick<Storage, 'setItem'> | null | undefined, on: boolean): void => {
    try {
        storage?.setItem(EXPERT_MODE_KEY, on ? 'on' : 'off');
    } catch {
        // Not remembered, nothing else.
    }
};
